import { $q } from "@uirouter/angularjs";
import angular, { IPromise, IQService, IScope } from "angular";
import {
  FormViewService,
  vsServiceName as FormViewServiceName,
  FormViewController,
  vsName,
} from "../../forms/formView";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../grids/columns/columnDefinitionService";
import { IRowCommandEventArgs } from "../../grids/controller";
import {
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../../views/recordGridView";
import { vsComponentName as lookupModalComponentOptionsName } from "./names";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../grids/settings/gridSettingsService";
import { LookupManagementService } from "../lookupManagement";
import { startCase } from "lodash";
import { ITemplateOptions } from "AngularFormly";
import {
  UserProfile,
  vsName as UserProfileName,
} from "../../../application/UserProfile";

export interface LookupModalOptions {
  name: string;
  lookupManagementService: LookupManagementService;
  model?: any;
  resultSelector?: (serverResponse: any) => any;
}

export class LookupModalService {
  static $inject = [
    "$uibModal",
    FormViewServiceName,
    RecordGridViewServiceName,
    ColumnDefinitionServiceName,
    GridSettingsServiceName,
    UserProfileName,
    "$q",
  ];

  constructor(
    private readonly $uibModal: angular.ui.bootstrap.IModalService,
    private readonly formViewService: FormViewService,
    private readonly recordGridViewService: RecordGridViewService,
    private readonly columnDefinitionService: ColumnDefinitionService,
    private readonly gridSettingsService: GridSettingsService,
    private readonly userProfile: UserProfile,
    private readonly $q: IQService
  ) {}

  openModal(config: LookupModalOptions) {
    const formViewConfig = this.formViewService.getFormViewConfig(config.name);
    const gridConfiguration = this.recordGridViewService.getGridConfiguration(
      config.name
    )!;
    const gridSettings = this.gridSettingsService.getGridSettings(
      Symbol("main.machines.lookups.grid." + config.name)
    );
    const lookupManagementConfig =
      config.lookupManagementService.getLookupConfig(config.name);
    gridConfiguration.columns = [
      this.columnDefinitionService.getCommandColDef({
        headerName: "select",
        iconClass: "fa fa-plus-circle text-primary",
        name: "select",
      }),
      ...gridConfiguration.columns!,
    ];

    gridConfiguration.onItemCommand = (args: IRowCommandEventArgs<any>) => {
      if (args.commandName === "select") {
        $uibModalInstance.close(args.data);
      }
      return $q.when();
    };

    const model = config.model ?? {};

    const $uibModalInstance = this.$uibModal.open({
      component: lookupModalComponentOptionsName,
      resolve: {
        config: () => config,
        formViewConfig: () => formViewConfig,
        gridConfiguration: () => gridConfiguration,
        gridSettings: () => gridSettings,
        model: () => model,
        displayName: () =>
          lookupManagementConfig.displayName ?? startCase(config.name),
        onSubmit: [
          () => (formViewController: FormViewController) => {
            const resultSelector = config.resultSelector ?? ((r) => r.Result);
            return formViewConfig?.submitForm!({
              formViewController,
              model,
            }).then((r) => $uibModalInstance.close(resultSelector(r)));
          },
        ],
        onCancel: [() => () => $uibModalInstance.dismiss("cancel")],
      },
    });

    return $uibModalInstance;
  }

  getDropdownAddAddon(
    lookupManagementService: LookupManagementService,
    lookupName: string,
    valueSetter: (value: any, scope: IScope, model: any) => void,
    permission: string,
    lookupModelFactory?: (
      options: ITemplateOptions,
      model: any,
      scope: IScope
    ) => IPromise<any>,
    resultSelector?: (serverResponse: any) => any
  ) {
    if (permission && !this.userProfile.hasPermission(permission)) {
      return undefined;
    }

    return {
      classAddon: "btn-secondary",
      class: "fa fa-plus-circle",
      onClick: (options: ITemplateOptions, model: any, scope: IScope) => {
        const selectAsyncOnSelect = (<any>scope).onSelect;
        (
          lookupModelFactory?.(options, model, scope) ?? this.$q.when(undefined)
        ).then((lookupModel) => {
          const modalInstance = this.openModal({
            name: lookupName,
            model: lookupModel,
            lookupManagementService,
            resultSelector,
          });
          modalInstance.result.then((result) => {
            selectAsyncOnSelect(result);
            valueSetter(result, scope, (<any>scope).model);
          });
        });
      },
    };
  }
}
